import React, { useState } from "react";
import { Link } from "react-router";
import { CheckCircle2, ChevronRight } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "../../app/components/ui/alert";
import { Button } from "../../app/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../app/components/ui/card";
import { Progress } from "../../app/components/ui/progress";
import { ProfileStepFields, type ProfileStepId } from "./components/ProfileStepFields";
import { useBusinessProfile } from "./hooks/useBusinessProfile";

const STEPS = [
  { id: "BASIC_INFO" as const, title: "Basic info", description: "Store type and display details" },
  { id: "LOCATION" as const, title: "Location", description: "Address" },
  { id: "CONTACT" as const, title: "Contact", description: "Phone and support" },
  { id: "COMPLIANCE" as const, title: "Compliance", description: "Licenses and tax IDs" },
  { id: "POS_CONFIGURATION" as const, title: "POS setup", description: "Defaults for checkout" },
];

export function BusinessProfileEditPage() {
  const { profile, error, loading, saving, updateProfile } = useBusinessProfile();

  const [activeStepId, setActiveStepId] = useState<ProfileStepId>("BASIC_INFO");
  const storeType = profile?.store_type ?? null;

  const [form, setForm] = useState<Record<string, string>>({});

  React.useEffect(() => {
    if (!profile?.profile_data) return;
    const key = activeStepId as keyof typeof profile.profile_data;
    const existing = profile.profile_data[key];
    if (existing && typeof existing === "object") {
      const next: Record<string, string> = {};
      for (const [k, v] of Object.entries(existing)) {
        if (k === "tax_inclusive_pricing" || k === "low_stock_alert_enabled") {
          next[k] = v === true || v === "true" || v === 1 ? "true" : "false";
        } else {
          next[k] = v === null || v === undefined ? "" : String(v);
        }
      }
      setForm(next);
    } else {
      setForm({});
    }
  }, [profile, activeStepId]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    const payload: Record<string, unknown> = { ...form };
    if (activeStepId === "POS_CONFIGURATION") {
      payload.tax_inclusive_pricing =
        form.tax_inclusive_pricing === "true" || form.tax_inclusive_pricing === "on";
    }
    const profileData: Record<string, Record<string, unknown>> = {
      [activeStepId]: payload as Record<string, unknown>,
    };
    const storeTypeUpdate =
      activeStepId === "BASIC_INFO" && form.store_type ? form.store_type : undefined;
    await updateProfile({
      profileData,
      storeType: storeTypeUpdate,
    });
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30">
        <p className="text-muted-foreground">Loading profile…</p>
      </div>
    );
  }

  if (!profile?.is_complete) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/30 p-6">
        <Card className="max-w-md w-full">
          <CardHeader>
            <CardTitle>Finish onboarding first</CardTitle>
            <CardDescription>Complete your business profile before editing.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button asChild className="w-full">
              <Link to="/onboarding/business-profile">Continue onboarding</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const progress = profile?.completion_percentage ?? 0;

  return (
    <div className="min-h-screen bg-muted/30 py-10 px-4">
      <div className="max-w-3xl mx-auto space-y-8">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Edit business profile</h1>
          <p className="text-muted-foreground mt-1">
            Update a step and save. Changes notify the admin team.
          </p>
        </div>

        <div className="space-y-2">
          <div className="flex justify-between text-sm text-muted-foreground">
            <span>Progress</span>
            <span>{progress}%</span>
          </div>
          <Progress value={progress} className="h-2" />
        </div>

        <ol className="flex flex-wrap gap-2">
          {STEPS.map((s, i) => {
            const done = profile?.completed_steps?.includes(s.id);
            const current = s.id === activeStepId;
            return (
              <li key={s.id}>
                <button
                  type="button"
                  onClick={() => setActiveStepId(s.id)}
                  className={[
                    "flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-medium transition-colors",
                    done ? "border-emerald-200 bg-emerald-50 text-emerald-800" : "",
                    current ? "border-primary bg-primary/5 text-primary ring-2 ring-primary/20" : "",
                    !done && !current ? "border-border text-muted-foreground hover:bg-accent" : "",
                  ].join(" ")}
                >
                  <span className="opacity-70">{i + 1}.</span>
                  {s.title}
                  {done ? <CheckCircle2 className="h-3.5 w-3.5 ml-1" /> : null}
                </button>
              </li>
            );
          })}
        </ol>

        {error ? (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        <Card>
          <CardHeader>
            <CardTitle>{STEPS.find((s) => s.id === activeStepId)?.title ?? "Step"}</CardTitle>
            <CardDescription>
              {STEPS.find((s) => s.id === activeStepId)?.description ?? ""}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <ProfileStepFields
                activeStepId={activeStepId}
                form={form}
                setForm={setForm}
                storeType={storeType}
              />

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={saving}>
                  {saving ? "Saving…" : "Save changes"}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/settings/business-profile" className="text-primary hover:underline">
            Back to profile view
          </Link>
        </p>
      </div>
    </div>
  );
}
